import Tooltip from "./Tooltip.tsx";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  Ref,
  useId,
  useState,
} from "react";
import { useIntl } from "react-intl";
import OffScreen from "./OffScreen.tsx";
import MountAfterDelay from "./MountWithDelay.tsx";

interface Status {
  message: string;
  time: number;
}

interface ButtonWithTooltipFeedbackProps
  extends Omit<
    ComponentPropsWithoutRef<"button">,
    "onClick" | "onBlur" | "onKeyUp" | "aria-describedby" | "data-tooltip-id"
  > {
  action: () => Promise<string>;
  defaultTooltip?: string;
}

function ButtonWithTooltipFeedback(
  { action, defaultTooltip, ...rest }: ButtonWithTooltipFeedbackProps,
  ref: Ref<HTMLButtonElement>
) {
  const [status, setStatus] = useState<Status | null>(null);
  const feedbackId = useId();
  const intl = useIntl();

  return (
    <>
      <button
        {...rest}
        ref={ref}
        data-tooltip-id={feedbackId}
        onClick={() => {
          action()
            .then((message) => {
              setStatus({ message, time: Date.now() });
            })
            .catch(() => {
              setStatus({
                message: intl.formatMessage({
                  defaultMessage: "Unexpected Error",
                }),
                time: Date.now(),
              });
            });
        }}
        onBlur={() => setStatus(null)}
        onKeyUp={(e) => {
          if (e.key === "Escape") {
            setStatus(null);
          }
        }}
      />
      {status ? (
        <Tooltip
          key={status.time}
          arrow
          id={feedbackId}
          isOpen
          afterHide={() => setStatus(null)}
        >
          <OffScreen role="status" aria-live="polite">
            <MountAfterDelay timeout={250}>{status.message}</MountAfterDelay>
          </OffScreen>
          <div aria-hidden="true">{status.message}</div>
        </Tooltip>
      ) : defaultTooltip ? (
        <Tooltip id={feedbackId}>{defaultTooltip}</Tooltip>
      ) : null}
    </>
  );
}

const ForwardRef = forwardRef(ButtonWithTooltipFeedback);
export default ForwardRef;
